"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Heart, Scale, Shield, Sword, Crown } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function LaJusticeCardPage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-green-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-green-500">/</span>
            <span className="text-sm">Marseille</span>
            <span className="text-green-500">/</span>
            <span className="text-sm">Major Arkana</span>
            <span className="text-green-500">/</span>
            <span className="text-sm">La Justice</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about/tarot/systems/marseille/major-arcana"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Major Arkana'ya Geri Dön
          </Link>
        </div>

        {/* Header with Card */}
        <div className="bg-gradient-to-r from-green-900/20 to-emerald-900/20 rounded-xl p-8 mb-8 border border-green-500/20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1 flex justify-center">
              <div className="w-48 h-72 rounded-xl overflow-hidden shadow-2xl">
                <img
                  src="/images/tarot/cards/marseille-la-justice.svg"
                  alt="La Justice Tarot Card"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.currentTarget;
                    target.style.display = 'none';
                    if (target.nextElementSibling) {
                      (target.nextElementSibling as HTMLElement).style.display = 'flex';
                    }
                  }}
                />
                <div className="hidden w-full h-full bg-gradient-to-b from-green-900/40 to-emerald-900/40 border border-green-500/30 flex-col items-center justify-center text-green-300">
                  <span className="text-3xl font-bold mb-2">VIII</span>
                  <span className="text-lg font-semibold">LA JUSTICE</span>
                  <span className="text-sm text-center mt-2">Justice</span>
                </div>
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-700 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">VIII</span>
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-slate-100">La Justice</h1>
                  <p className="text-xl text-green-400">Adalet / Denge ve Hakkaniyet</p>
                </div>
              </div>
              <p className="text-lg text-slate-200 leading-relaxed mb-4">
                Marseille geleneğinde sekizinci numara. Denge, adalet ve karmanın kartı.
                Objektiflik, hukuk ve etik değerlerin temsilcisidir. Doğru kararların
                alınması ve sorumluluklarının yerine getirilmesini ifade eder.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm">Adalet</span>
                <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm">Denge</span>
                <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm">Karma</span>
                <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm">Hukuk</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card Meanings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Positive Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Scale className="h-6 w-6 text-green-400" />
              Düz Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Adalet ve Hakkaniyet</h3>
                  <p className="text-slate-400 text-sm">Doğru kararların alınması ve adaletli davranma.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Denge ve Harmoni</h3>
                  <p className="text-slate-400 text-sm">Yaşamın her alanında denge kurma ve istikrar.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Karma ve Sonuçlar</h3>
                  <p className="text-slate-400 text-sm">Geçmiş eylemlerinin karşılığını alma, adil sonuçlar.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Hukuki Başarı</h3>
                  <p className="text-slate-400 text-sm">Yasal işlemlerde olumlu sonuçlar, hak elde etme.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Negative Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Sword className="h-6 w-6 text-red-400" />
              Ters Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Adaletsizlik</h3>
                  <p className="text-slate-400 text-sm">Haksızlık, yanlış kararlar ve taraflı davranma.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Dengesizlik</h3>
                  <p className="text-slate-400 text-sm">Yaşam alanlarında istikrarsızlık ve uyumsuzluk.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Hukuki Sorunlar</h3>
                  <p className="text-slate-400 text-sm">Yasal problemler, davalar ve gecikmiş adalet.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Sorumluluk Kaçırma</h3>
                  <p className="text-slate-400 text-sm">Hesap verememe, görevleri ihmal etme.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Symbolism */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Marseille Sembolizmi</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <Scale className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Terazi</h3>
              <p className="text-slate-400 text-sm">Denge, ölçme ve adaletli değerlendirme.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <Sword className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Kılıç</h3>
              <p className="text-slate-400 text-sm">Keskin karar verme ve gerçeği ortaya çıkarma.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <Crown className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Taç</h3>
              <p className="text-slate-400 text-sm">Otorite, hikmet ve yargıcı güç.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">👗</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Kırmızı Elbise</h3>
              <p className="text-slate-400 text-sm">Tutku, güç ve kararlılığı simgeler.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🧥</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Mor Manto</h3>
              <p className="text-slate-400 text-sm">Ruhsal otorite ve yüce adaleti temsil eder.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-gray-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🏛️</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Sütunlar</h3>
              <p className="text-slate-400 text-sm">İstikrar, güç ve adaletin temelleri.</p>
            </div>
          </div>
        </div>

        {/* Life Areas */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yaşam Alanlarındaki Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Heart className="h-5 w-5 text-pink-400" />
                Aşk ve İlişkiler
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• İlişkide denge ve adalet</li>
                <li>• Eşit sorumluluk paylaşımı</li>
                <li>• Doğru partnerle buluşma</li>
                <li>• Geçmiş ilişki karmalarının çözümü</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-400" />
                Kariyer ve İş
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Hukuk ve adalet alanları</li>
                <li>• Haklı davalar ve başarılar</li>
                <li>• Etik iş yapma</li>
                <li>• Adil ücret ve tanınma</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Scale className="h-5 w-5 text-green-400" />
                Kişisel Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Objektif bakış açısı</li>
                <li>• Adil karar verme yetisi</li>
                <li>• Sorumluluk alma</li>
                <li>• Etik değerlerin gelişmesi</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Shield className="h-5 w-5 text-blue-400" />
                Hukuk ve Finans
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Yasal işlemlerde başarı</li>
                <li>• Adil sözleşmeler</li>
                <li>• Mali dengenin sağlanması</li>
                <li>• Hakkaniyet prensibi</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/marseille/major-arcana/le-chariot"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Önceki: Le Chariot
          </Link>

          <Link
            href="/about/tarot/systems/marseille/major-arcana/l-ermite"
            className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
          >
            Sonraki: L'Ermite
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}